import {
  applySelectReadOnly, dateFieldHtml, escapeHtml, formatNum, mountDateField, notchedFieldHtml,
  type MoneyCurrency,
} from '@colplat/ui-web';
import { apiFetch, failureMessage } from '../api.js';
import type { TagDto } from './webledgerTags.js';
import type { CurrencyDto } from './webledgerOfflineState.js';
import type { FieldValue } from '@colplat/protocol';
import { bsModal } from './webledgerTransactionEditor.js';

/**
 * Budgets: a limit for one tagged project, out of one wallet (ADR-0024).
 *
 * The page's whole job is to put two numbers next to each other. One of them
 * somebody typed; the other is the same `SUM()` the reports page performs
 * under a tag filter, which is why every row links through to exactly that
 * filtered list — a "spent" figure that disagreed with the list behind it
 * would discredit both.
 *
 * Two things are stated rather than hidden, and both are consequences of a tag
 * not being a partition the way a wallet is. A transaction funded from outside
 * the budget's wallet is named rather than dropped, because a budget that
 * silently under-reports is worse than no budget. And a transaction carrying
 * two budgeted tags counts in full against both, so each row says how much it
 * shares and with whom.
 *
 * Colour carries nothing on its own (invariant #11): every bar is accompanied
 * by the same fact in words, and "over" is a word before it is a shade.
 */

/** Where "nearly there" starts. A budget that is 90% gone is worth noticing before it is spent. */
export const NEAR_LIMIT = 0.9;

/*
 * The shape and the arithmetic both come from `apps/webledger/domain` now
 * (ADR-0025), so this panel and `services/api` cannot disagree about what a
 * budget has cost — which matters here more than anywhere, because each row
 * links straight through to the list it claims to be a total of.
 */
import type { BudgetDto } from '@colplat/app-webledger/domain';

export type { BudgetDto };

export interface BudgetProgress {
  limit: number;
  /** Spent in the limit's OWN currency. Anything else is in `otherCurrencies`. */
  spent: number;
  /** Signed: positive is headroom, negative is the overrun. */
  left: number;
  ratio: number;
  /** 0..1, for the bar's width. The overrun is carried by the words, not by a bar off the end of its track. */
  fill: number;
  state: 'under' | 'near' | 'over';
  otherCurrencies: Array<{ currencyCode: string; amount: string }>;
}

/**
 * The arithmetic, separated from the paint.
 *
 * Money spent in a currency the limit is not written in is deliberately NOT
 * folded in, at any rate, ever. A wallet is short in forints or in euros,
 * never just short (ADR-0023), and a budget is no different: converting here
 * would bury a rate inside a number the user believes they typed.
 */
export function budgetProgress(budget: BudgetDto): BudgetProgress {
  const limit = Number(budget.amount);
  const spent = Number(budget.spent[budget.currencyCode] ?? 0);
  const ratio = limit > 0 ? spent / limit : 0;
  const otherCurrencies = Object.entries(budget.spent)
    .filter(([code]) => code !== budget.currencyCode)
    .map(([currencyCode, amount]) => ({ currencyCode, amount }))
    .sort((a, b) => a.currencyCode.localeCompare(b.currencyCode));

  return {
    limit,
    spent,
    left: limit - spent,
    ratio,
    fill: Math.max(0, Math.min(1, ratio)),
    state: ratio > 1 ? 'over' : ratio >= NEAR_LIMIT ? 'near' : 'under',
    otherCurrencies,
  };
}

export type MoneyFormatter = (value: number | string, currencyCode: string) => string;

/**
 * The headline, in words.
 *
 * Says what is left rather than what is used, because that is the question
 * being asked of a budget — "can we still afford the tiles" — and a percentage
 * alone never answers it. The percentage follows, for the glance.
 */
export function budgetSentence(p: BudgetProgress, ccy: string, fmt: MoneyFormatter): string {
  const percent = `${Math.round(p.ratio * 100)}% used`;
  if (p.state === 'over') return `Over by ${fmt(-p.left, ccy)} — ${percent}`;
  if (p.limit <= 0) return percent;
  return `${fmt(p.left, ccy)} left — ${percent}`;
}

/**
 * The things a reader has to be told before they trust the number above.
 *
 * Both are honest admissions rather than warnings about their behaviour, so
 * they are phrased as facts about the figure, not as advice.
 */
export function budgetCaveats(
  budget: BudgetDto, p: BudgetProgress, ccy: string, fmt: MoneyFormatter,
  nameOf: (budgetId: string) => string,
): string[] {
  const out: string[] = [];
  const amounts = (totals: Record<string, string>): string =>
    Object.entries(totals).map(([code, v]) => fmt(v, code)).join(' and ');

  if (p.otherCurrencies.length > 0) {
    /*
     * Direction as a word, magnitude as a number.
     *
     * An INCOME leg is negative, so the obvious phrasing produces "Also
     * -50,000.00 USD spent" — which reads as a strange way of writing a
     * refund and invites the reader to wonder whether the sign is a bug.
     * Saying "back" and printing the amount unsigned says the same thing in
     * the words people use for it.
     */
    const parts = p.otherCurrencies.map((o) => {
      const value = Number(o.amount);
      return `${fmt(Math.abs(value), o.currencyCode)} ${value < 0 ? 'back' : 'spent'}`;
    });
    const one = parts.length === 1;
    out.push(`Also in ${one ? 'another currency' : 'other currencies'}: ${parts.join(', ')}. `
      + `A ${ccy} limit cannot count ${one ? 'it' : 'them'}.`);
  }
  if (budget.outsideWallet.count > 0) {
    const n = budget.outsideWallet.count;
    out.push(`${n} transaction${n === 1 ? '' : 's'} on this project `
      + `(${amounts(budget.outsideWallet.totals)}) came out of another wallet, so it is not counted here.`);
  }
  if (budget.sharedWith.count > 0) {
    const n = budget.sharedWith.count;
    const others = budget.sharedWith.budgetIds.map(nameOf).filter(Boolean);
    out.push(`${n} transaction${n === 1 ? '' : 's'} `
      + `(${amounts(budget.sharedWith.totals)}) also count${n === 1 ? 's' : ''} in full against `
      + `${others.length > 0 ? others.join(' and ') : 'another budget'}.`);
  }
  return out;
}

/** How many budgets are asking for attention — what the tab badge counts. */
export function overBudgetCount(budgets: readonly BudgetDto[]): number {
  return budgets.filter((b) => budgetProgress(b).state === 'over').length;
}

/** The period, as a phrase. Empty when it runs open at both ends, which is the common case. */
export function periodText(budget: BudgetDto): string {
  if (budget.fromDate && budget.toDate) return `${budget.fromDate} to ${budget.toDate}`;
  if (budget.fromDate) return `from ${budget.fromDate}`;
  if (budget.toDate) return `until ${budget.toDate}`;
  return '';
}

// -------------------------------------------------------------------- markup

export function budgetsPaneHtml(): string {
  return `
    <section id="wlPane-budgets" data-pane="budgets" role="tabpanel"
      aria-labelledby="wlTab-budgets" tabindex="-1" hidden>
      <div class="cf-card p-3">
        <div class="d-flex flex-wrap align-items-end gap-2 mb-2 position-relative">
          <div class="cf-h flex-grow-1">Budgets</div>
          <!-- Same "?" treatment as the log: the standing explanation is the
               same sentence every time you look at this page, which is worth
               reading once and not worth a paragraph for ever. -->
          <span class="cf-help cf-help--left mb-1">
            <button type="button" class="cf-help__btn" id="wlBudHelpBtn"
              aria-label="What a budget is here" aria-expanded="false"
              aria-controls="wlBudHelpPanel">?</button>
            <span class="cf-help__panel" id="wlBudHelpPanel" role="note"></span>
          </span>
          <!-- Only offered when something is actually put away: a switch for
               an empty set is a question about nothing, and it would sit here
               for ever on books that never hide a budget. -->
          <div class="form-check form-switch mb-1 small" id="wlBudShowHiddenWrap" hidden>
            <input class="form-check-input" type="checkbox" role="switch" id="wlBudShowHidden">
            <label class="form-check-label" for="wlBudShowHidden">Show hidden</label>
          </div>
          <button class="btn btn-sm btn-outline-primary mb-1" type="button" id="wlBudNewBtn">New budget</button>
        </div>
        <div id="wlBudList"></div>
      </div>
    </section>`;
}

export function budgetDialogHtml(): string {
  return `
  <div class="modal fade" id="wlBudDlg" tabindex="-1" aria-hidden="true" data-bs-backdrop="static">
    <div class="modal-dialog wl-modal--quick" data-dlg-panel>
      <div class="modal-content">
        <form>
          <div class="modal-header py-2 d-flex justify-content-between align-items-center gap-2" data-dlg-handle>
            <span class="cf-drag-grip" aria-hidden="true">&#10303;</span>
            <h2 class="h6 mb-0 flex-grow-1" id="wlBudTitle">New budget</h2>
          </div>
          <div class="modal-body">
            <div class="alert alert-warning py-2 px-3 small" id="wlBudWarn" role="status" aria-live="polite" hidden></div>
            <div class="row g-2">
              <div class="col-12">
                ${notchedFieldHtml('Project (tag)',
                  '<select id="wlBudTag" class="wl-nf__ctl" aria-label="Project"></select>')}
              </div>
              <div class="col-12 col-sm-7">
                ${notchedFieldHtml('Whose money',
                  '<select id="wlBudWallet" class="wl-nf__ctl" aria-label="Whose money"></select>')}
              </div>
              <!-- Why the two above stop responding once a budget exists. It is
                   a sentence rather than a disabled control on purpose: invariant
                   #4 forbids a disabled control, and a reader who cannot see why a field
                   will not move is left thinking the page is broken. -->
              <div class="col-12 small text-muted" id="wlBudFixed" hidden>The project and the wallet
                are what this budget <em>is</em>. To cap a different one, make a new budget.</div>
              <div class="col-12 col-sm-5">
                ${notchedFieldHtml('Limit',
                  '<span class="wl-nf__pair">'
                  + '<input id="wlBudAmount" class="wl-nf__ctl text-end cf-no-spin" type="number"'
                  + ' min="0" placeholder="0" aria-label="Limit">'
                  + '<select id="wlBudCcy" class="wl-nf__ctl wl-nf__ctl--auto" aria-label="Currency"></select>'
                  + '</span>')}
              </div>
              <div class="col-6">
                ${notchedFieldHtml('From (optional)', dateFieldHtml('wlBudFrom', 'From'))}
              </div>
              <div class="col-6">
                ${notchedFieldHtml('Until (optional)', dateFieldHtml('wlBudTo', 'Until'))}
              </div>
              <div class="col-12">
                ${notchedFieldHtml('Note',
                  '<input id="wlBudNote" class="wl-nf__ctl" type="text" maxlength="255" aria-label="Note">')}
              </div>
              <!-- Put away, not deleted. A finished project stops being
                   something to look at and goes on being something to look
                   up; deleting it would answer the first need by destroying
                   the second. The sentence says which, because "hidden" on
                   its own invites the reader to wonder what it costs. -->
              <div class="col-12">
                <div class="form-check form-switch">
                  <input class="form-check-input" type="checkbox" role="switch" id="wlBudHidden">
                  <label class="form-check-label small" for="wlBudHidden">Hidden —
                    finished, and kept for looking up</label>
                </div>
              </div>
            </div>
            <!-- The free alternative, said where the decision is being made
                 rather than left to be discovered. ADR-0024 is explicit that
                 both routes are supported; this is the half of that promise
                 the user can actually see. -->
            <p class="small text-muted mt-3 mb-0">A budget is money you <em>plan</em>. If you would
              rather set the cash aside for real, make a wallet inside this one and move the money
              in — then going over shows up as an ordinary shortfall, and the wallets add up as
              they always did.</p>
          </div>
          <div class="modal-footer py-2">
            <button type="button" class="btn btn-sm btn-outline-danger me-auto" id="wlBudDelete" hidden>Delete</button>
            <button type="button" class="btn btn-sm btn-outline-secondary" data-bs-dismiss="modal">Cancel</button>
            <button type="submit" class="btn btn-sm btn-primary" id="wlBudSave">Save</button>
          </div>
        </form>
      </div>
    </div>
  </div>`;
}

const HELP_TEXT = 'A budget caps what one tagged project may cost out of one wallet. '
  + 'Spending counts the way the reports page counts it under a tag filter — the expense and '
  + 'income legs, never the money that funded them. Going over never blocks an entry; it shows up here.';

// ------------------------------------------------------------------ mounting

export interface BudgetsHost {
  root: HTMLElement;
  token: string;
  notify: (message: string) => void;
  tags: () => readonly TagDto[];
  currencies: () => readonly CurrencyDto[];
  money: (code: string) => MoneyCurrency;
  /** Every budget with its spend, computed from the local replica by the host. */
  budgets: () => readonly BudgetDto[];
  /** Every wallet, for the picker and for naming one. */
  walletOptions: () => ReadonlyArray<{ id: string; label: string }>;
  walletName: (id: string) => string;
  /** Show the transactions behind a budget: the log for its wallet, filtered to its tag. */
  openLog: (walletId: string | null, tagId: string) => void;
  /** Budgets changed — the tab badge and anything else counting them needs a repaint. */
  onChange: () => void;
  newId: () => string;
  /**
   * Claim, commit and release one field — the same path the wallet editor's
   * parent select takes.
   *
   * A budget is `editMode: 'field'`, so an edit belongs on the lease/history
   * machinery rather than on a REST round trip: raising a limit from 4M to 5M
   * is exactly the kind of change somebody will later want to see, and two
   * people changing one budget at once should be arbitrated rather than
   * silently last-write-wins. Returns false when somebody else holds it.
   */
  commitField: (recordId: string, field: string, value: FieldValue | null) => Promise<boolean>;
  /**
   * Set a toggle, which takes no lease.
   *
   * `hidden` is a `bool`, and a bool defaults to compare-and-swap with no
   * lease (CLAUDE.md: "Toggles use compare-and-swap with no lease"). Sending
   * it through `commitField` asks for a lease on a field the server will not
   * lease, and the claim is refused — which the dialog then reported as
   * somebody else editing the budget, because that is what a refused claim
   * usually means.
   */
  setToggle: (recordId: string, field: string, wanted: boolean) => Promise<boolean>;
  openRecord: (recordId: string) => Promise<void>;
  closeRecord: (recordId: string) => void;
}

export interface MountedBudgets {
  /** Re-read from the replica. Synchronous now — nothing is fetched. */
  load: () => void;
  render: () => void;
  budgets: () => readonly BudgetDto[];
}

export function mountBudgets(host: BudgetsHost): MountedBudgets {
  const el = <T extends HTMLElement>(id: string): T => host.root.querySelector<T>(`#${id}`)!;
  let budgets: BudgetDto[] = [];
  /** The budget being edited, or undefined while creating one. */
  let editing: BudgetDto | undefined;

  const fmt: MoneyFormatter = (value, code) => `${formatNum(value, host.money(code))} ${code}`;
  const tagName = (id: string): string => host.tags().find((t) => t.id === id)?.name ?? 'a deleted tag';
  const budgetName = (id: string): string => {
    const other = budgets.find((b) => b.id === id);
    return other ? tagName(other.tagId) : '';
  };

  /** Computed from the local replica — the same `budgetsView` the server runs. */
  function load(): void {
    budgets = [...host.budgets()];
  }

  function rowHtml(budget: BudgetDto): string {
    const p = budgetProgress(budget);
    const ccy = budget.currencyCode;
    const period = periodText(budget);
    const caveats = budgetCaveats(budget, p, ccy, fmt, budgetName);
    const where = budget.walletId ? host.walletName(budget.walletId) : 'any wallet';

    return `<div class="wl-bud wl-bud--${p.state}">
      <div class="wl-bud__head">
        <div class="wl-bud__who">
          <span class="wl-bud__name">${escapeHtml(tagName(budget.tagId))}</span>
          <span class="wl-bud__where">out of ${escapeHtml(where)}</span>
          ${period ? `<span class="wl-bud__when">${escapeHtml(period)}</span>` : ''}
        </div>
        <div class="wl-bud__acts">
          <button type="button" class="btn btn-sm btn-outline-secondary"
            data-bud-show="${escapeHtml(budget.id)}">Show the ${budget.count} transaction${budget.count === 1 ? '' : 's'}</button>
          <button type="button" class="wl-wal__edit" data-bud-edit="${escapeHtml(budget.id)}"
            title="Change this budget" aria-label="Change this budget">&#9998;</button>
        </div>
      </div>
      <div class="wl-bud__bar">
        <!-- The bar is never the only signal (invariant #11): the same fact is
             spelled out beside it, and role="img" with a label is what makes
             it readable to somebody who never sees the fill at all. -->
        <span class="wl-bud__track" role="img"
          aria-label="${escapeHtml(`${fmt(p.spent, ccy)} of ${fmt(p.limit, ccy)}`)}">
          <span class="wl-bud__fill" style="width:${(p.fill * 100).toFixed(1)}%"></span>
        </span>
      </div>
      <div class="wl-bud__figures">
        <span class="wl-bud__spent">${escapeHtml(fmt(p.spent, ccy))} of ${escapeHtml(fmt(p.limit, ccy))}</span>
        <span class="wl-bud__left">${escapeHtml(budgetSentence(p, ccy, fmt))}</span>
      </div>
      ${budget.note ? `<p class="wl-bud__note">${escapeHtml(budget.note)}</p>` : ''}
      ${caveats.length > 0
        ? `<ul class="wl-bud__caveats">${caveats.map((c) => `<li>${escapeHtml(c)}</li>`).join('')}</ul>`
        : ''}
    </div>`;
  }

  function render(): void {
    const list = el('wlBudList');
    /*
     * Hidden budgets are left out of the list, and out of nothing else.
     *
     * The flag is about what this page offers unasked — the totals, the
     * spending and every other number a hidden budget takes part in are
     * computed exactly as before. "Finished" is a judgement the user makes,
     * not an arithmetic fact about `toDate`: a project can be done early or
     * run past its own deadline.
     */
    // A replicated `bool` arrives as MySQL's 1/0, never a JavaScript
    // `true` — so it is read truthily, as every other toggle in this app
    // is. `=== true` quietly matched nothing.
    const putAway = budgets.filter((b) => Boolean(b.hidden));
    el('wlBudShowHiddenWrap').hidden = putAway.length === 0;
    const showHidden = el<HTMLInputElement>('wlBudShowHidden').checked;
    const shown = showHidden ? budgets : budgets.filter((b) => !b.hidden);

    if (budgets.length === 0) {
      list.innerHTML = '<p class="small text-muted mb-0">No budgets yet. A budget caps what one '
        + 'tagged project may cost — tag the transactions that belong to it, then set the limit here.</p>';
      return;
    }
    // Over first, then nearly there: the rows that ask something of the reader
    // go where the reader already is, rather than wherever they were created.
    const rank = { over: 0, near: 1, under: 2 } as const;
    if (shown.length === 0) {
      list.innerHTML = `<p class="small text-muted mb-0">Every budget here is hidden.
        ${escapeHtml(String(putAway.length))} put away — turn on “Show hidden” to see them.</p>`;
      return;
    }
    const sorted = [...shown].sort((a, b) => {
      const byState = rank[budgetProgress(a).state] - rank[budgetProgress(b).state];
      return byState !== 0 ? byState : tagName(a.tagId).localeCompare(tagName(b.tagId));
    });
    list.innerHTML = sorted.map(rowHtml).join('');

    list.querySelectorAll<HTMLButtonElement>('[data-bud-show]').forEach((b) => {
      b.addEventListener('click', () => {
        const budget = budgets.find((x) => x.id === b.dataset['budShow']);
        if (budget) host.openLog(budget.walletId, budget.tagId);
      });
    });
    list.querySelectorAll<HTMLButtonElement>('[data-bud-edit]').forEach((b) => {
      b.addEventListener('click', () => {
        const budget = budgets.find((x) => x.id === b.dataset['budEdit']);
        if (budget) openEditor(budget);
      });
    });
  }

  // ------------------------------------------------------------ the editor

  function fillPickers(): void {
    const tagSel = el<HTMLSelectElement>('wlBudTag');
    tagSel.innerHTML = host.tags()
      .map((t) => `<option value="${escapeHtml(t.id)}">${escapeHtml(t.name)}</option>`).join('');

    const walletSel = el<HTMLSelectElement>('wlBudWallet');
    walletSel.innerHTML = '<option value="">Any wallet</option>'
      + host.walletOptions()
        .map((w) => `<option value="${escapeHtml(w.id)}">${escapeHtml(w.label)}</option>`).join('');

    const ccySel = el<HTMLSelectElement>('wlBudCcy');
    ccySel.innerHTML = host.currencies()
      .map((c) => `<option value="${escapeHtml(c.code)}">${escapeHtml(c.code)}</option>`).join('');
  }

  function openEditor(budget?: BudgetDto): void {
    editing = budget;
    fillPickers();
    el('wlBudWarn').hidden = true;
    el('wlBudTitle').textContent = budget ? 'Change this budget' : 'New budget';
    el('wlBudDelete').hidden = !budget;
    el<HTMLSelectElement>('wlBudTag').value = budget?.tagId ?? (host.tags()[0]?.id ?? '');
    el<HTMLSelectElement>('wlBudWallet').value = budget?.walletId ?? '';
    el<HTMLInputElement>('wlBudAmount').value = budget ? String(Number(budget.amount)) : '';
    // A currency the book actually keeps totals in, rather than whichever one
    // happens to sort first — the list holds eight and the ledger is run in
    // three, so the alphabetical answer is wrong seven times out of eight.
    const preferred = host.currencies().find((c) => c.useForTotals) ?? host.currencies()[0];
    el<HTMLSelectElement>('wlBudCcy').value = budget?.currencyCode ?? (preferred?.code ?? '');
    el<HTMLInputElement>('wlBudFrom').value = budget?.fromDate ?? '';
    el<HTMLInputElement>('wlBudTo').value = budget?.toDate ?? '';
    el<HTMLInputElement>('wlBudNote').value = budget?.note ?? '';
    el<HTMLInputElement>('wlBudHidden').checked = Boolean(budget?.hidden);

    /*
     * The project and the wallet are fixed once a budget exists.
     *
     * Not a technical limit — they are ordinary fields. Changing either turns
     * the record into a budget for a different thing while keeping its
     * history, so "this budget went from 4M to 5M" would silently mean two
     * unrelated projects. Deleting and creating says what actually happened.
     *
     * `readonly` is not available on a select, so this is the one place
     * `disabled` would be reached for — and invariant #4 forbids it. The
     * select is therefore left alone and the SAVE ignores both fields, with
     * the reason shown rather than the control taken away.
     */
    el('wlBudFixed').hidden = !budget;
    applySelectReadOnly(el<HTMLSelectElement>('wlBudTag'), budget !== undefined);
    applySelectReadOnly(el<HTMLSelectElement>('wlBudWallet'), budget !== undefined);

    const modalEl = el('wlBudDlg');
    modalEl.addEventListener('shown.bs.modal', () => {
      el<HTMLInputElement>('wlBudAmount').focus();
    }, { once: true });
    bsModal(modalEl)?.show();
  }

  function warn(message: string): void {
    const box = el('wlBudWarn');
    box.textContent = message;
    box.hidden = false;
  }

  async function save(): Promise<void> {
    const amount = el<HTMLInputElement>('wlBudAmount').value.trim();
    if (!(Number(amount) > 0)) { warn('A budget has to be more than nothing.'); return; }
    const from = el<HTMLInputElement>('wlBudFrom').value.trim();
    const to = el<HTMLInputElement>('wlBudTo').value.trim();
    if (from && to && from > to) { warn('That ends before it starts.'); return; }
    const currencyCode = el<HTMLSelectElement>('wlBudCcy').value;
    const note = el<HTMLInputElement>('wlBudNote').value.trim();
    const hidden = el<HTMLInputElement>('wlBudHidden').checked;

    const ok = editing
      ? await applyEdits(editing, { amount, currencyCode, fromDate: from, toDate: to, note, hidden })
      : await createBudget({ amount, currencyCode, fromDate: from, toDate: to, note, hidden });
    if (!ok) return;

    bsModal(el('wlBudDlg'))?.hide();
    load();
    render();
    host.onChange();
  }

  interface BudgetForm {
    amount: string; currencyCode: string; fromDate: string; toDate: string; note: string;
    hidden: boolean;
  }

  async function createBudget(form: BudgetForm): Promise<boolean> {
    const res = await apiFetch('/apps/webledger/budgets', {
      token: host.token,
      method: 'POST',
      body: {
        id: host.newId(),
        tagId: el<HTMLSelectElement>('wlBudTag').value,
        walletId: el<HTMLSelectElement>('wlBudWallet').value,
        ...form,
      },
    }).catch(() => undefined);
    if (!res?.ok) { warn(await failureMessage(res, 'Could not save that.')); return false; }
    return true;
  }

  /*
   * An edit writes only what actually changed, field by field, each through
   * its own claim/commit/release.
   *
   * Only what changed, because every commit is a history row and a save that
   * rewrote all five would fill the panel with "amount: 5000000 → 5000000".
   * Field by field rather than in one request, because that is what
   * `editMode: 'field'` means here and it is the same path the wallet
   * editor's parent select takes — there is no cursor to protect in any of
   * these controls, so the lease is taken at the moment of writing and let go
   * immediately.
   *
   * The project and the wallet are deliberately absent: they are what the
   * budget IS, and changing them would keep one record's history across two
   * unrelated things.
   */
  async function applyEdits(budget: BudgetDto, form: BudgetForm): Promise<boolean> {
    const wanted: Array<[string, FieldValue | null, string]> = [
      ['amount', { t: 'decimal', v: form.amount }, String(Number(budget.amount))],
      ['currencyCode', { t: 'string', v: form.currencyCode }, budget.currencyCode],
      ['fromDate', form.fromDate ? { t: 'date', v: form.fromDate } : { t: 'null', v: null }, budget.fromDate ?? ''],
      ['toDate', form.toDate ? { t: 'date', v: form.toDate } : { t: 'null', v: null }, budget.toDate ?? ''],
      ['note', form.note ? { t: 'string', v: form.note } : { t: 'null', v: null }, budget.note ?? ''],
    ];
    const next: Record<string, string> = {
      amount: String(Number(form.amount)),
      currencyCode: form.currencyCode,
      fromDate: form.fromDate,
      toDate: form.toDate,
      note: form.note,
    };

    // The toggle first, and outside the lease: it takes none, and a failure
    // here should not leave the leased fields half-written.
    if (form.hidden !== Boolean(budget.hidden)
      && !await host.setToggle(budget.id, 'hidden', form.hidden)) {
      warn('Could not change whether this budget is hidden.');
      return false;
    }

    await host.openRecord(budget.id);
    try {
      for (const [field, value, before] of wanted) {
        if (next[field] === before) continue;
        if (!await host.commitField(budget.id, field, value)) {
          warn('Somebody else is changing this budget right now. Try again in a moment.');
          return false;
        }
      }
    } finally {
      host.closeRecord(budget.id);
    }
    return true;
  }

  async function remove(): Promise<void> {
    if (!editing) return;
    const res = await apiFetch(`/apps/webledger/budgets/${editing.id}`, {
      token: host.token, method: 'DELETE',
    }).catch(() => undefined);
    if (!res?.ok) { warn(await failureMessage(res, 'Could not remove that.')); return; }
    bsModal(el('wlBudDlg'))?.hide();
    load();
    render();
    host.onChange();
  }

  // ------------------------------------------------------------------ wiring

  el('wlBudHelpPanel').textContent = HELP_TEXT;
  el('wlBudShowHidden').addEventListener('change', render);

  el('wlBudNewBtn').addEventListener('click', () => {
    if (host.tags().length === 0) {
      host.notify('A budget needs a project to cap. Make a tag first, then tag the transactions that belong to it.');
      return;
    }
    openEditor();
  });
  el('wlBudSave').addEventListener('click', (event) => { event.preventDefault(); void save(); });
  el('wlBudDlg').querySelector('form')?.addEventListener('submit', (event) => {
    event.preventDefault();
    void save();
  });
  el('wlBudDelete').addEventListener('click', () => { void remove(); });

  for (const id of ['wlBudFrom', 'wlBudTo']) {
    const input = el<HTMLInputElement>(id);
    const button = el('wlBudDlg').querySelector<HTMLElement>(`[data-date-open="${id}"]`);
    if (button) mountDateField(input, button);
  }

  return { load, render, budgets: () => budgets };
}
